import {yupResolver} from "@hookform/resolvers/yup";
import {FormProvider, useForm} from "react-hook-form";
import {FiArrowRight} from "react-icons/fi";
import {useLocation, useSearchParams} from "react-router";
import * as Yup from "yup";

import {AssistantIcon} from "@/components/assistant/AssistantIcon.tsx";
import {Button} from "@/components/button/Button.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {SIGNUP_QUERY_PARAM} from "@/lib/constants/account.ts";
import {AnalyzerSource} from "@/lib/queries/analyzerRuns.ts";
import {ANALYZER_PRIVATE_IPV4_PATTERN, ANALYZER_URL_PATTERN} from "@/pages/analyzer/Analyzer.constants.ts";
import {normalizeUrl} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

import styles from "./AnalyzerEntryScreen.module.scss";

const AnalyzerUrlSchema = Yup.object().shape({
  url: Yup.string()
    .required("Please enter a URL")
    .max(2048, "Please enter a shorter URL")
    .test("valid-url", "Please enter a valid URL", (value) => {
      if (!value) return false;
      const normalized = normalizeUrl(value);
      if (!ANALYZER_URL_PATTERN.test(normalized)) return false;
      try {
        const hostname = new URL(normalized).hostname;
        return !(/^\d+(\.\d+){3}$/.test(hostname) && ANALYZER_PRIVATE_IPV4_PATTERN.test(hostname));
      } catch {
        return false;
      }
    }),
});

interface AnalyzerUrlFormValues {
  url: string;
}

export const AnalyzerEntryScreen = () => {
  const {handleSubmit: submitAnalysis} = useAnalyzerContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const methods = useForm<AnalyzerUrlFormValues>({
    resolver: yupResolver<AnalyzerUrlFormValues>(AnalyzerUrlSchema),
    defaultValues: {url: searchParams.get("url") ?? ""},
  });
  const fallbackSource = searchParams.has(SIGNUP_QUERY_PARAM) ? AnalyzerSource.ONBOARDING : AnalyzerSource.DIRECT;
  const source = (location.state?.source as AnalyzerSource | undefined) ?? fallbackSource;

  const handleSubmit = methods.handleSubmit((values) => submitAnalysis(normalizeUrl(values.url), source));

  return (
    <div className={styles.entryScreen}>
      <div className={styles.agent}>
        <AssistantIcon size="large" />
        <div className={styles.agentStatus}>
          <span className={styles.agentName}>Uptime Assistant</span>
          <span className={styles.readyIndicator}>
            <Dot variant="gradientGreen" size="large" />
            Ready to analyze
          </span>
        </div>
      </div>
      <div className={styles.main}>
        <div className={styles.intro}>
          <div className={styles.title}>What should we monitor?</div>
          <div className={styles.description}>
            <div>
              Enter a URL and our Assistant will map its infrastructure and propose the exact Checks worth running.
            </div>
            <div>Review, refine by chat, and deploy in one click.</div>
          </div>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputWrapper}>
              <InputField name="url" placeholder="example.com" autoFocus />
            </div>
            <Button type="submit" variant="primary" size="small" disabled={methods.formState.isSubmitting}>
              Analyze
              <Icon icon={FiArrowRight} removeDivDimensions />
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
